import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';

import { getHttpHeaders } from '@commons/utility/util';

import { COOKIE_ENUMERATION, SESSION_STORAGE_ENUMERATION } from '@home/login/configs';

import { environment } from '@environments/environment';

import { LoginException } from '@account/exceptions/login-exception';
import { CustomResponseDto } from '@core/model/custom-response-dto';
import { AuthenticationResponse } from '@core/model/response/authentication-response';
import { StopFlowResponse } from '@core/model/response/stop-flow-response';

import { User } from '@model/user';
import { LocalService } from '@home/storage/local.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  apiUrl = environment.apiUrl;

  private domain = environment.domain;

  constructor(
    private cookieService: CookieService,
    private httpClient: HttpClient,
    private router: Router,
    private localService: LocalService
  ) {}

  private _user: User;

  public get user(): User {
    if (this._user != null) {
      return this._user;
    } else if (this._user == null && this.localService.getJsonValue('user') != null) {
      this._user = JSON.parse(this.localService.getJsonValue('user')) as User;
      return this._user;
    }
    return new User();
  }

  private _token: string;

  public get token(): string {
    if (this._token != null) {
      return this._token;
    } else if (this._token == null && this.localService.getJsonValue('token') != null) {
      this._token = this.localService.getJsonValue('token');
      return this._token;
    }
    return null;
  }

  login(user: User) {
    const urlEndpoint = `${environment.apiGatewayUrl}security/auth`;
    user.secret = btoa('frontClientApp' + ':' + '12345');
    return this.httpClient.post(urlEndpoint, JSON.stringify(user), { headers: getHttpHeaders() });
  }

  saveUser(accessToken: string): void {
    const payload = this.getTokenData(accessToken);
    this._user = new User();
    this._user.name = payload.nombre;
    this._user.email = payload.email;
    this._user.username = payload.user_name;
    this._user.roles = payload.authorities;

    this.localService.setJsonValue('user', JSON.stringify(this._user));
  }

  saveToken(accessToken: string): void {
    this._token = accessToken;
    this.localService.setJsonValue('token', this._token);
    localStorage.setItem('userToken', this._token);
  }

  getTokenData(accessToken: string) {
    try {
      return JSON.parse(atob(accessToken.split('.')[1]));
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const payload = this.getTokenData(this.token);
    if (payload != null && payload.user_name && payload.user_name.length > 0) {
      return true;
    }
    return false;
  }

  public logout(): void {
    this._token = null;
    this._user = null;
    this.clearStorage();
    this.deleteCookies();
    this.redirectToLogin();
  }

  private clearStorage(): void {
    localStorage.clear();
    sessionStorage.removeItem(SESSION_STORAGE_ENUMERATION.IS_NEW_LOAN);
    sessionStorage.removeItem(SESSION_STORAGE_ENUMERATION.AMOUNT);
    sessionStorage.removeItem(SESSION_STORAGE_ENUMERATION.TERM);
    sessionStorage.removeItem(SESSION_STORAGE_ENUMERATION.QUOTA);
  }

  private deleteCookies(): void {
    this.cookieService.delete(COOKIE_ENUMERATION.CRFRAP, '/', this.domain);
    this.cookieService.delete(COOKIE_ENUMERATION.ORIGIN, '/', this.domain);
    this.cookieService.delete(COOKIE_ENUMERATION.STATUS, '/', this.domain);
    this.cookieService.delete(COOKIE_ENUMERATION.TERM, '/', this.domain);
    this.cookieService.delete(COOKIE_ENUMERATION.AMOUNT, '/', this.domain);
    this.cookieService.delete(COOKIE_ENUMERATION.QUOTA, '/', this.domain);
    this.cookieService.delete(COOKIE_ENUMERATION.IS_NEW_LOAN, '/', this.domain);
    this.cookieService.delete(COOKIE_ENUMERATION.MY_CREDITS_LOGOUT, '/', this.domain);
  }

  private redirectToLogin(): void {
    this.router.navigate(['/login'], { queryParamsHandling: 'preserve' }).then(() => {
      window.location.reload();
    });
  }

  getRoles() {
    const payload = this.getTokenData(this.token);
    if (payload != null && payload.roles && payload.roles.length > 0) {
      return payload.roles;
    }
  }

  hasRole(role: string): boolean {
    if (this.user.roles.includes(role)) {
      return true;
    }
    return false;
  }

  isTokenExpired(): boolean {
    const token = this.token;
    const payload = this.getTokenData(token);
    const now = new Date().getTime() / 1000;
    if (payload.exp < now) {
      return true;
    }
    return false;
  }

  public verifyLoginResponse(): OperatorFunction<
    CustomResponseDto<AuthenticationResponse & StopFlowResponse>,
    AuthenticationResponse & StopFlowResponse
  > {
    return (source: Observable<CustomResponseDto<AuthenticationResponse & StopFlowResponse>>) =>
      source.pipe(
        map((loginResponse: CustomResponseDto<AuthenticationResponse & StopFlowResponse>) => {
          if (loginResponse.status == '200' || loginResponse.status == null) {
            return loginResponse.response;
          } else {
            throw new LoginException(
              loginResponse.response.message,
              loginResponse.response.urlRedirect,
              loginResponse.response.stopFlowReason
            );
          }
        })
      );
  }
}
